import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Radio,
  Snackbar,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import {
  AutoAwesome,
  Lock,
  PriorityHigh,
  VerifiedUser,
} from '@mui/icons-material';
import { apiClient } from '../../services/api';

type AutonomyMode = 'read_only' | 'approval_required' | 'autonomous';

interface AssistantConfig {
  mode: AutonomyMode;
  autoBook: {
    priceCap: number;
    dailyLimit: number;
    minVisitCount: number;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

interface ModeOption {
  value: AutonomyMode;
  title: string;
  subtitle: string;
  icon: React.ReactElement;
  badges: string[];
}

const MODE_OPTIONS: ModeOption[] = [
  {
    value: 'read_only',
    title: 'Read-only',
    subtitle: 'Assistant answers questions and quotes prices. It cannot book — customers are told to call the salon.',
    icon: <Lock />,
    badges: ['Safest'],
  },
  {
    value: 'approval_required',
    title: 'Approval required',
    subtitle: 'Assistant proposes slots via WhatsApp buttons. Every booking goes to you for a one-tap approval.',
    icon: <VerifiedUser />,
    badges: ['Default', 'Recommended'],
  },
  {
    value: 'autonomous',
    title: 'Autonomous',
    subtitle: 'Assistant books directly when guardrails pass (regular customer, standard price, business hours, daily cap).',
    icon: <AutoAwesome />,
    badges: ['Fastest'],
  },
];

interface Props {
  salonId: string;
}

const AssistantSettings: React.FC<Props> = ({ salonId }) => {
  const [config, setConfig] = useState<AssistantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<ApiResponse<AssistantConfig>>(`/settings/${salonId}/assistant`)
      .then((res) => {
        if (cancelled) return;
        setConfig(res.data);
        setError(null);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Could not load assistant settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [salonId]);

  const setMode = (mode: AutonomyMode) => {
    if (!config) return;
    setConfig({ ...config, mode });
  };

  const setAutoBookField = (field: keyof AssistantConfig['autoBook'], value: number) => {
    if (!config) return;
    setConfig({ ...config, autoBook: { ...config.autoBook, [field]: value } });
  };

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const res = await apiClient.put<ApiResponse<AssistantConfig>>(
        `/settings/${salonId}/assistant`,
        config
      );
      setConfig(res.data);
      setToast({ msg: 'Assistant settings saved', severity: 'success' });
    } catch (err) {
      setToast({ msg: (err as Error).message || 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const showGuardrails = useMemo(() => config?.mode === 'autonomous', [config]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !config) {
    return <Alert severity="error">{error ?? 'Unknown error'}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>
        AI Assistant
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
        Controls how the WhatsApp lead assistant behaves when customers message your salon.
        Choose an autonomy mode and optional booking guardrails.
      </Typography>

      <Stack spacing={2}>
        {MODE_OPTIONS.map((opt) => (
          <ModeCard
            key={opt.value}
            option={opt}
            selected={config.mode === opt.value}
            onSelect={() => setMode(opt.value)}
          />
        ))}
      </Stack>

      {showGuardrails && (
        <Paper variant="outlined" sx={{ mt: 4, p: 3, borderStyle: 'solid' }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <PriorityHigh sx={{ color: 'warning.main', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Autonomous booking guardrails
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            The assistant confirms a booking automatically only when <strong>every</strong> condition below passes.
            Bookings that fail any check fall back to approval-required mode, and the reason is logged.
          </Typography>
          <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
            <TextField
              label="Service price cap"
              type="number"
              value={config.autoBook.priceCap}
              onChange={(e) => setAutoBookField('priceCap', parseInt(e.target.value || '0', 10))}
              helperText="Auto-book only services up to this price. 0 disables the cap."
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              fullWidth
            />
            <TextField
              label="Customer minimum visit count"
              type="number"
              value={config.autoBook.minVisitCount}
              onChange={(e) => setAutoBookField('minVisitCount', parseInt(e.target.value || '0', 10))}
              helperText="Only auto-book customers who have visited at least this many times. 0 allows new customers."
              fullWidth
            />
            <TextField
              label="Daily auto-book limit"
              type="number"
              value={config.autoBook.dailyLimit}
              onChange={(e) => setAutoBookField('dailyLimit', parseInt(e.target.value || '0', 10))}
              helperText="Maximum bookings per day the assistant can confirm on its own. Extras fall back to approval."
              fullWidth
            />
          </Stack>
        </Paper>
      )}

      <Divider sx={{ my: 4 }} />

      <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
        <Button variant="contained" onClick={save} disabled={saving} size="large">
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Stack>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

const ModeCard: React.FC<{ option: ModeOption; selected: boolean; onSelect: () => void }> = ({
  option,
  selected,
  onSelect,
}) => (
  <Card
    onClick={onSelect}
    sx={{
      cursor: 'pointer',
      border: '2px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: selected ? (t) => alpha(t.palette.primary.main, 0.05) : 'background.paper',
      transition: 'border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease',
      '&:hover': {
        borderColor: selected ? 'primary.main' : 'primary.light',
      },
    }}
    elevation={0}
  >
    <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', py: 2.5 }}>
      <Radio checked={selected} sx={{ p: 0.5 }} />
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          bgcolor: selected ? 'primary.light' : (t) => alpha(t.palette.text.primary, 0.05),
          color: selected ? 'primary.dark' : 'text.secondary',
        }}
      >
        {option.icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {option.title}
          </Typography>
          {option.badges.map((b) => (
            <Box
              key={b}
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                bgcolor: (t) => alpha(t.palette.secondary.main, 0.12),
                color: 'secondary.dark',
                fontSize: '0.6875rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {b}
            </Box>
          ))}
        </Stack>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
          {option.subtitle}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

export default AssistantSettings;
